
// Performance optimization utilities

// Enhanced preloading with priority and resource hints
export const preloadCriticalImages = () => {
  // High-priority critical assets
  const criticalAssets = [
    '/img/logo.svg',
    '/img/co.svg',
    '/img/ev.svg'
  ];
  
  // Secondary assets loaded after critical ones
  const secondaryAssets = [
    '/img/Comp1.gif',
    '/img/icon01.svg',
    '/img/icon02.svg'
  ];
  
  // Preload critical assets immediately with browser priority hints
  criticalAssets.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = src;
    link.as = src.endsWith('.svg') ? 'image' : 'image';
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    
    // Create image and mark as critical
    const img = new Image();
    img.src = src;
    img.dataset.critical = 'true';
  });
  
  // Use Intersection Observer to lazy-load secondary assets
  if ('IntersectionObserver' in window) {
    // Set up intersection observer for lazy loading
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    }, { rootMargin: '200px 0px' }); // Start loading when within 200px of viewport
    
    // Find all images with data-src attribute and observe them
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
    
    // Load secondary assets with a slight delay using requestIdleCallback
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        secondaryAssets.forEach(src => {
          const img = new Image();
          img.src = src;
          img.loading = 'lazy';
        });
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        secondaryAssets.forEach(src => {
          const img = new Image();
          img.src = src;
          img.loading = 'lazy';
        });
      }, 100);
    }
  } else {
    // Fallback for browsers without IntersectionObserver
    // Explicitly guard against SSR environments
    if (typeof window !== 'undefined') {
      // Fix: Use a properly typed window reference
      const safeWindow = window as Window & typeof globalThis;
      safeWindow.addEventListener('load', () => {
        secondaryAssets.forEach(src => {
          const img = new Image();
          img.src = src;
        });
      });
    }
  }
};

// Add function to optimize images after page load
export const optimizeImages = () => {
  // Convert img tags to use lazy loading where appropriate
  const imgElements = document.querySelectorAll('img:not([loading])');
  imgElements.forEach(img => {
    // Don't lazy load images that might be above the fold
    const rect = img.getBoundingClientRect();
    const isAboveTheFold = rect.top < window.innerHeight;
    
    if (!isAboveTheFold) {
      img.setAttribute('loading', 'lazy');
    }
    
    // Add decoding async for better performance
    img.setAttribute('decoding', 'async');
  });
  
  // Use native lazy loading where supported
  if ('loading' in HTMLImageElement.prototype) {
    const images = document.querySelectorAll('img[data-src]');
    images.forEach(img => {
      img.setAttribute('loading', 'lazy');
    });
  }
};

// Optimize script loading
export const optimizeScriptLoading = () => {
  // Find all non-critical scripts
  const scripts = document.querySelectorAll('script:not([data-critical])');
  
  // Defer their loading
  scripts.forEach(script => {
    if (!script.hasAttribute('defer') && !script.hasAttribute('async')) {
      script.setAttribute('defer', '');
    }
  });
  
  // Add connection preload hints for third-party domains
  const domains = ['cdnjs.cloudflare.com', 'unpkg.com'];
  domains.forEach(domain => {
    if (!document.querySelector(`link[rel="preconnect"][href="https://${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = `https://${domain}`;
      document.head.appendChild(link);
    }
  });
};

// Add function to debounce scroll events for better performance
export const setupOptimizedEventListeners = () => {
  let lastScrollTime = 0;
  const scrollThrottle = 100; // ms
  
  // Fix: Properly guard against SSR environments
  if (typeof window !== 'undefined') {
    // Fix: Create a properly typed reference to window
    const safeWindow = window as Window & typeof globalThis;
    
    safeWindow.addEventListener('scroll', () => {
      const now = Date.now();
      if (now - lastScrollTime > scrollThrottle) {
        lastScrollTime = now;
        // Your scroll handler logic
      }
    }, { passive: true }); // Passive true improves scroll performance
  }
};

// Performance monitoring utility (stub)
export const setupPerformanceMonitoring = () => {
  if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
    try {
      // Monitor Core Web Vitals
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Report Core Web Vitals metrics
          const metric = entry as PerformanceEntry;
          console.info(`${metric.name}: ${metric.startTime.toFixed(2)} seconds`);
        });
      });
      
      // Observe LCP and CLS metrics
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      observer.observe({ type: 'layout-shift', buffered: true });
      
      return observer;
    } catch (e) {
      console.warn('Performance monitoring not supported:', e);
    }
  }
  return null;
};

// Function to prevent layout shifts
export const preventLayoutShifts = () => {
  // Add fixed dimensions to image containers
  document.querySelectorAll('.swiper-slide img').forEach(img => {
    const imgElement = img as HTMLImageElement;
    if (!imgElement.width && !imgElement.height) {
      imgElement.style.aspectRatio = '16/9';
      imgElement.style.width = '100%';
    }
  });
};
